// @flow
import { colors } from 'bench-style-constants';
import {
  ButtonLink,
  Checkbox,
  checkboxStates,
  formatCentsAsDollars,
  HighlightKeyword,
  Icon,
  TextBlock,
  Text,
} from 'bench-internal-theme';
import { PersonIdContext } from 'bk-web-app';
import { format, parse } from 'date-functions';
// $FlowFixMe - TS file
import { SelectedDataEntriesConsumer } from 'general-ledger';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import type { DataEntry } from './types';

const FollowUpDataEntryRowLayout = styled(TextBlock)`
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.silt};
  box-shadow: 0 -1px 0 ${colors.silt};
  cursor: pointer;
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: 15px 72px minmax(118px, 1fr) 72px 72px 16px 220px minmax(118px, 1fr) 16px 16px;
  margin: 0 32px;
  position: relative;
  padding: 8px;
  align-items: start;

  &:hover {
    background-color: ${colors.brook};
  }

  ${({ isSelected }) => isSelected && `background-color: ${colors.brook}`};
`;

const EllipsisText = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NotesLayout = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-column-gap: 8px;
  grid-row-gap: 16px;
`;

const isCurrentPersonComment = (clientComment, currentPersonId) =>
  clientComment.author && clientComment.author.id === currentPersonId;

type FollowUpViewDataEntryRowProps = {
  dataEntry: DataEntry,
  isSelected?: boolean,
  search: string,
  toggleDataEntry: DataEntry => void,
};

const FollowUpViewDataEntryRow = ({
  dataEntry,
  isSelected,
  search,
  toggleDataEntry,
}: FollowUpViewDataEntryRowProps) => {
  const { amountInCents, journalEntry, ledgerAccount } = dataEntry;

  const { addOn, categorization, clientComments, dateOfTransaction, document, vendorName } =
    journalEntry;

  const suggestedLedgerAccountId =
    categorization &&
    categorization.selectedLedgerAccount &&
    categorization.selectedLedgerAccount.id;

  const categorizationUIClientComment =
    categorization && categorization.categorizationUIClientComment;

  const hasClientSuggestedCurrentLedger =
    ledgerAccount && ledgerAccount.id === suggestedLedgerAccountId;

  return (
    <FollowUpDataEntryRowLayout
      styleType="small"
      isSelected={isSelected}
      onMouseDown={() => toggleDataEntry(dataEntry)}
    >
      <SelectedDataEntriesConsumer>
        {({ isDataEntryIdSelected, addSelectedDataEntryIds, removeSelectedDataEntryIds }) => (
          <ButtonLink
            onMouseDown={mouseEvent => {
              // Prevent the sidebar from toggling
              mouseEvent.stopPropagation();
              if (isDataEntryIdSelected(dataEntry.id)) {
                removeSelectedDataEntryIds(dataEntry.id);
              } else {
                addSelectedDataEntryIds(dataEntry.id);
              }
            }}
          >
            <Checkbox
              checkboxState={
                isDataEntryIdSelected(dataEntry.id)
                  ? checkboxStates.CHECKED
                  : checkboxStates.UNCHECKED
              }
            />
          </ButtonLink>
        )}
      </SelectedDataEntriesConsumer>
      <Text styleType="small" color={colors.cloud}>
        {format(parse(dateOfTransaction), 'MM/DD/YYYY')}
      </Text>
      <EllipsisText>
        <HighlightKeyword keyword={search}>{vendorName}</HighlightKeyword>
      </EllipsisText>
      <TextBlock align="right" styleType="small" color="#217950">
        {amountInCents >= 0 && formatCentsAsDollars(Math.abs(amountInCents))}
      </TextBlock>
      <TextBlock align="right" styleType="small" color="#ae280e">
        {amountInCents < 0 && formatCentsAsDollars(Math.abs(amountInCents))}
      </TextBlock>
      <div>
        {hasClientSuggestedCurrentLedger && (
          <Icon color={colors.dust} icon="circleCheck" size="small" />
        )}
      </div>
      <EllipsisText>{ledgerAccount && ledgerAccount.name}</EllipsisText>
      <NotesLayout>
        {categorizationUIClientComment && (
          <Fragment>
            <Icon color={colors.water} icon="shoppingTag" size="small" />
            <Text styleType="small">
              <HighlightKeyword keyword={search}>{categorizationUIClientComment}</HighlightKeyword>
            </Text>
          </Fragment>
        )}
        {clientComments &&
          clientComments.map(comment => (
            <PersonIdContext.Consumer key={comment.id}>
              {personId => (
                <Fragment>
                  <Icon
                    color={isCurrentPersonComment(comment, personId) ? colors.gold : colors.water}
                    icon="house"
                    size="small"
                  />
                  <Text styleType="small">
                    <HighlightKeyword keyword={search}>{comment.body}</HighlightKeyword>
                  </Text>
                </Fragment>
              )}
            </PersonIdContext.Consumer>
          ))}
      </NotesLayout>
      <div>
        {document && document.id && <Icon color={colors.dust} icon="paperclip" size="small" />}
      </div>
      <div>{addOn && addOn.id && <Icon color={colors.dust} icon="link" size="small" />}</div>
    </FollowUpDataEntryRowLayout>
  );
};

FollowUpViewDataEntryRow.defaultProps = {
  isSelected: false,
};

export default FollowUpViewDataEntryRow;
